import { useCallback, useEffect, useState } from "react";
import { httpsCallable } from "firebase/functions";
import { functions } from "../firebase";

/* The admin passphrase gate.
 *
 * On /admin/checkout this is the whole gate — that route sits outside
 * PrivateRoute, so the key is the only credential asked for. /admin/tools also
 * requires a Firebase login on top of it.
 *
 * Shares SESSION_KEY with /admin/tools on purpose: verifying once covers both
 * admin pages for the life of the session, rather than asking for the key again
 * on every hop between them.
 */

const SESSION_KEY = "adminToolsToken";

export default function useAdminToken() {
  const [token, setToken] = useState(null);
  const [verified, setVerified] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    try {
      const raw = sessionStorage.getItem(SESSION_KEY);
      if (!raw) return;
      const obj = JSON.parse(raw);
      if (obj && obj.token && obj.expiresAt && Date.now() < obj.expiresAt) {
        setToken(obj.token);
        setVerified(true);
      } else {
        sessionStorage.removeItem(SESSION_KEY);
      }
    } catch {}
  }, []);

  // No length check here on purpose. verifyAdminKey applies the real rule and
  // is the only opinion that counts; a second copy in the client just drifts
  // from it and tells the user their key is wrong before anything has looked
  // at it.
  const verify = useCallback(async (key) => {
    setBusy(true);
    try {
      const verifyAdminKey = httpsCallable(functions, "verifyAdminKey");
      const res = await verifyAdminKey({ key });
      const data = res.data;
      if (!data || !data.ok) throw new Error((data && data.error) || "Verify failed");
      sessionStorage.setItem(
        SESSION_KEY,
        JSON.stringify({ token: data.token, expiresAt: data.expiresAt })
      );
      setToken(data.token);
      setVerified(true);
    } finally {
      setBusy(false);
    }
  }, []);

  const clear = useCallback(() => {
    sessionStorage.removeItem(SESSION_KEY);
    setToken(null);
    setVerified(false);
  }, []);

  return { token, verified, busy, verify, clear };
}
